import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useApps, useEnvVars, useProjects } from '@/api/hooks';
import { envVarFilters, isScopeReady, scopeFromSearch, scopeSearch, tabSearch, visibleRows, type EnvPageSearch } from './envPageModel';
import type { ScopeSelection } from './ScopePicker';

/** Scope changes and tab switches both rewrite the URL (replace, so Back does not step through every pick). */
function useEnvNavigation(slug: string, search: EnvPageSearch) {
  const navigate = useNavigate();
  const [inherited, setInherited] = useState(false);
  const setScope = (next: ScopeSelection) => {
    setInherited(next.includeInherited ?? false);
    void navigate({ to: '/projects/$slug/env', params: { slug }, search: scopeSearch(next, search.tab), replace: true });
  };
  const setTab = (tab: string) => void navigate({ to: '/projects/$slug/env', params: { slug }, search: tabSearch(search, tab), replace: true });
  return { inherited, setScope, setTab };
}

/** Screen 06 data: the project, its apps, the scope from the URL and the variables listed at that scope. */
export function useEnvPage(slug: string, search: EnvPageSearch) {
  const projects = useProjects();
  const project = projects.data?.find((p) => p.slug === slug);
  const apps = useApps(project?.id);
  const nav = useEnvNavigation(slug, search);
  const scope = useMemo(() => scopeFromSearch(search, apps.data, project), [search, apps.data, project]);
  const app = apps.data?.find((a) => a.slug === scope.appSlug);
  const scopeReady = isScopeReady(scope, Boolean(project), Boolean(app));
  const filters = useMemo(() => envVarFilters(scope, project?.id, app?.id, nav.inherited), [scope, project?.id, app?.id, nav.inherited]);
  const vars = useEnvVars(filters, scopeReady);
  const rows = useMemo(() => visibleRows(vars.data, scope), [vars.data, scope]);
  return { projects, project, apps, scope, scopeReady, vars, rows, ...nav };
}

export type EnvPageApi = ReturnType<typeof useEnvPage>;
